import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '@msi/cobalt-react/icon';
import Button from '@msi/cobalt-react/button';
import { Modal, ModalHeader, ModalContent, ModalActions } from '@msi/cobalt-react/modal';
import { useOktaAuth } from '@okta/okta-react';
import SubscriptionService, { UserSubscription } from '../../services/subscriptionService';
import { addToast } from '@msi/cobalt-react/toast';
import './ManageSubscriptionsModal.css';

interface ManageSubscriptionsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const subscriptionService = new SubscriptionService();

const ManageSubscriptionsModal: React.FC<ManageSubscriptionsModalProps> = ({ isOpen, onClose }) => {
    const { authState } = useOktaAuth();
    const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
    const [removedSubscriptionIds, setRemovedSubscriptionIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const modalStyle = useMemo(() => ({
        overlay: {
            zIndex: 4000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
        },
        content: {
            zIndex: 4001,
            inset: 'auto',
            position: 'relative',
            border: 'none',
            padding: 0,
            overflow: 'visible',
            width: 'min(650px, 100vw)',
            maxWidth: '650px',
            margin: '0 auto',
            background: 'transparent',
        },
    }), []);

    const visibleSubscriptions = useMemo(
        () => subscriptions.filter((subscription) => !removedSubscriptionIds.has(subscription.id)),
        [subscriptions, removedSubscriptionIds]
    );

    const hasChanges = removedSubscriptionIds.size > 0;

    const resetState = () => {
        setRemovedSubscriptionIds(new Set());
        setError(null);
    };

    const fetchSubscriptions = useCallback(async () => {
        if (!authState) {
            setError('Authentication is required to load subscriptions.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await subscriptionService.getMySubscriptions(authState);
            setSubscriptions(result);
            setRemovedSubscriptionIds(new Set());
        } catch (fetchError) {
            console.error(fetchError);
            setError('Unable to load your subscriptions right now.');
        } finally {
            setLoading(false);
        }
    }, [authState]);

    useEffect(() => {
        if (isOpen) {
            fetchSubscriptions();
        } else {
            resetState();
        }
    }, [isOpen, fetchSubscriptions]);

    const handleUnsubscribeAll = () => {
        if (saving || !visibleSubscriptions.length) {
            return;
        }
        setRemovedSubscriptionIds(new Set(subscriptions.map((subscription) => subscription.id)));
    };

    const handleSave = async () => {
        if (!hasChanges || saving) {
            return;
        }

        if (!authState) {
            addToast('error', 'You must be authenticated to update subscriptions.', {
                containerId: 'default',
            });
            return;
        }

        setSaving(true);

        try {
            await subscriptionService.unsubscribeBatch(Array.from(removedSubscriptionIds), authState);
            addToast('success', 'Your subscription preferences have been successfully updated.', {
                containerId: 'default',
            });
            onClose();
        } catch (saveError) {
            console.error(saveError);
            addToast('error', 'Unable to update subscriptions right now. Please try again later.', {
                containerId: 'default',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Modal handleCloseModal={handleCancel} showModal={isOpen} style={modalStyle}>
            <ModalHeader>Update Subscriptions</ModalHeader>
            <ModalContent>
                <div className="manage-subscriptions-body">
                    <button
                        type="button"
                        className="manage-subscriptions-unsubscribe-all"
                        onClick={handleUnsubscribeAll}
                        disabled={saving || !visibleSubscriptions.length}
                    >
                        Unsubscribe all
                    </button>
                    {error && <div className="manage-subscriptions-error">{error}</div>}
                    {loading ? (
                        <div className="manage-subscriptions-loading">Loading subscriptions...</div>
                    ) : visibleSubscriptions.length ? (
                        <ul className="manage-subscriptions-list">
                            {visibleSubscriptions.map((subscription) => (
                                <li key={subscription.id} className="manage-subscriptions-list-item">
                                    <div className="manage-subscriptions-item-details">
                                        <a 
                                            href={subscriptionService.getApiDetailsUrl(subscription.apiId)}
                                            className="manage-subscriptions-api-name"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {subscription.apiName}
                                        </a>
                                        <span className="manage-subscriptions-email">{subscription.notificationEmail || subscription.userEmail}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="manage-subscriptions-remove-btn"
                                        onClick={() => {
                                            if (!saving) {
                                                setRemovedSubscriptionIds((prev) => {
                                                    const updated = new Set(prev);
                                                    updated.add(subscription.id);
                                                    return updated;
                                                });
                                            }
                                        }}
                                        aria-label={`Remove subscription for ${subscription.apiName}`}
                                        disabled={saving}
                                    >
                                        <Icon name="ic_remove" size={24} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="manage-subscriptions-empty-state">
                            You are not subscribed to any APIs yet.
                        </div>
                    )}
                </div>
            </ModalContent>
            <ModalActions>
                <Button 
                    text="Cancel" 
                    onClick={handleCancel}
                    disabled={saving}
                />
                <Button 
                    text={saving ? 'Saving...' : 'Save'}
                    onClick={handleSave} 
                    variant="primary"
                    disabled={saving || !hasChanges}
                />
            </ModalActions>
        </Modal>
    );
};

export default ManageSubscriptionsModal;